import * as Sentry from '@sentry/react';
import { getAppEnv, isProdEnv } from '@/config/env';

export function initSentry() {
  const appEnv = getAppEnv();

  if (appEnv === 'dev') return;

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: appEnv,
    release: process.env.GIT_COMMIT_HASH || 'unknown',

    tracesSampleRate: 1.0,
    replaysSessionSampleRate: isProdEnv() ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      ...(isProdEnv() ? [Sentry.replayIntegration()] : []),
    ],

    ignoreErrors: ['ResizeObserver loop', 'Network Error', 'LOGOUT_DRAINING'],

    _experiments: {
      enableLogs: true,
    },
  });
}

/**
 * 通过 Sentry.logger 发送结构化日志到 Sentry Logs 面板
 */
export function sentryLog(
  level: 'debug' | 'info' | 'warn' | 'error' | 'fatal',
  message: string,
  attrs?: Record<string, any>,
) {
  if (getAppEnv() === 'dev') return;

  const user = Sentry.getCurrentScope().getUser();
  const userAttrs: Record<string, string> = {};
  if (user?.id) userAttrs.user_id = String(user.id);
  if (user?.username) userAttrs.user_name = user.username;
  if (user?.segment) userAttrs.user_role = user.segment;

  const merged = { ...userAttrs, ...(attrs ?? {}) };

  const logAttrs = Object.fromEntries(
    Object.entries(merged).map(([k, v]) =>
      typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
        ? [k, v]
        : [k, String(v)],
    ),
  );

  Sentry.logger[level](message, logAttrs);
}

/**
 * Redacts sensitive Authorization headers from Axios-style error objects
 * before they are sent to Sentry. Mutates the error in-place and returns it.
 */
export function redactSentryError<T>(error: T): T {
  if (!error || typeof error !== 'object') return error;
  const errAny = error as any;

  const config = errAny.config;
  if (
    config &&
    typeof config === 'object' &&
    config.headers &&
    typeof config.headers === 'object'
  ) {
    if ('Authorization' in config.headers)
      config.headers.Authorization = '<redacted>';
    if ('authorization' in config.headers)
      config.headers.authorization = '<redacted>';
  }

  const request = errAny.request;
  if (request && typeof request === 'object') {
    for (const headerProp of ['_headers', 'headers'] as const) {
      const headers = (request as any)[headerProp];
      if (headers && typeof headers === 'object') {
        if ('Authorization' in headers) headers.Authorization = '<redacted>';
        if ('authorization' in headers) headers.authorization = '<redacted>';
      }
    }
  }

  return error;
}

/**
 * 同时上报到 Issues（captureException/captureMessage）和 Logs
 */
export function captureError(
  error: Error | string,
  context?: Record<string, any>,
) {
  if (getAppEnv() === 'dev') return;

  if (typeof error === 'string') {
    Sentry.captureMessage(error, { level: 'error', extra: context });
  } else {
    Sentry.captureException(redactSentryError(error), { extra: context });
  }

  sentryLog(
    'error',
    typeof error === 'string' ? error : error.message,
    context,
  );
}

export function captureWarning(message: string, context?: Record<string, any>) {
  if (getAppEnv() === 'dev') return;

  Sentry.captureMessage(message, { level: 'warning', extra: context });
  sentryLog('warn', message, context);
}

export function setSentryUser(user: {
  id: string;
  name?: string;
  role?: string;
}) {
  Sentry.setUser({ id: user.id, username: user.name, segment: user.role });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}
